import {
    Component, inject, signal, computed, CUSTOM_ELEMENTS_SCHEMA,
    resource
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { lastValueFrom, tap } from 'rxjs';
import { ModuleService, SIDEBAR_GROUPS } from '../../../shared/services/module.service';
import { SystemModule } from '../../../shared/models';
import { AppButton } from '../../../shared/components/app-button/app-button';
import { AppBadge } from '../../../shared/components/app-badge/app-badge';
import { AppDataTable, DataColumn, PaginationState } from '../../../shared/components/app-data-table/app-data-table';
import { AppModal } from '../../../shared/components/app-modal/app-modal';
import { AppMetrics, MetricItem } from '../../../shared/components/app-metrics/app-metrics';

@Component({
    selector: 'app-system-module-list',
    standalone: true,
    imports: [CommonModule, RouterLink, AppButton, AppBadge, AppDataTable, AppModal, AppMetrics],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './system-module-list.html',
})
export class SystemModuleList {
    private moduleService = inject(ModuleService);

    // ── Parámetros de consulta ──────────────────────────────────────────────
    page = signal(1);
    limit = signal(10); // Default coincidente con el select de la tabla
    searchQ = signal('');

    // ── Detalle ─────────────────────────────────────────────────────────────
    selectedModule = signal<SystemModule | null>(null);
    showDetails = signal(false);

    // ── Resource (Siguiendo patrón ANA_frontend) ──────────────────────────
    moduleResource = resource({
        params: () => ({
            page: this.page(),
            limit: this.limit(),
            search: this.searchQ()
        }),
        loader: async ({ params }) => {
            const { page, limit, search } = params;
            return lastValueFrom(
                this.moduleService.loadAll({ all: true, page, limit, search }).pipe(
                    tap(res => {
                        this.modules.set(res?.data ?? []);
                        this.total.set(res?.total ?? 0);
                        this.stats.set(res?.stats ?? {});
                    })
                )
            );
        }
    });

    // ── Estado ──────────────────────────────────────────────────────────────
    modules = signal<SystemModule[]>([]);
    total = signal(0);
    stats = signal<Record<string, number>>({});

    /** Objeto de paginación para la app-data-table */
    pagination = computed<PaginationState>(() => ({
        current: this.page(),
        limit: this.limit(),
        total: this.total()
    }));

    loading = this.moduleResource.isLoading;

    readonly groups = SIDEBAR_GROUPS;

    metricsItems = computed<MetricItem[]>(() =>
        this.groups.map(g => ({
            key: g.key,
            label: g.label,
            value: this.countByGroup(g.key),
            icon: g.icon,
            variant: 'primary',
            clickable: true
        }))
    );

    // ── Columnas ─────────────────────────────────────────────────────────────
    columns: DataColumn[] = [
        { key: 'label', title: 'Módulo', dataIndex: 'label' },
        { key: 'group', title: 'Grupo', dataIndex: 'group' },
        { key: 'permissions', title: 'Permisos', dataIndex: 'permissions' },
        { key: 'isActive', title: 'Estado', dataIndex: 'isActive' },
        { key: 'actions', title: '', align: 'right', class: 'w-24' },
    ];

    // ── Events (Disparan el Resource) ───────────────────────────────────────

    handleSearch(q: string) {
        this.searchQ.set(q);
        this.page.set(1);
    }

    handlePageChange(p: number) {
        this.page.set(p);
    }

    handleLimitChange(l: number) {
        this.limit.set(l);
        this.page.set(1);
    }

    handleReload() {
        this.moduleResource.reload();
    }

    // ── Helpers ──────────────────────────────────────────────────────────────

    countByGroup(group: string): number {
        return this.stats()[group] ?? 0;
    }

    deleteModule(mod: SystemModule) {
        if (!confirm(`¿Eliminar "${mod.label}"? Esta acción no se puede deshacer.`)) return;
        this.moduleService.delete(mod._id).subscribe({
            next: () => this.moduleResource.reload(),
            error: (err) => console.error('Error al eliminar módulo', err),
        });
    }

    viewDetails(mod: SystemModule) {
        this.selectedModule.set(mod);
        this.showDetails.set(true);
    }

    syncModules() {
        this.moduleService.sync().subscribe({
            next: (res) => {
                alert(`Sincronización completada: ${res.created} creados, ${res.updated} actualizados.`);
                this.moduleResource.reload();
            },
            error: (err) => console.error('Error al sincronizar módulos', err),
        });
    }
}
